import { useAppSelector } from "@store";
import { getAccount, getIsSubscribed } from "@store/account/selectors";
import { useEffect, useRef, useState } from "react";

const EARLY_BIRDS_PERIOD_IN_MINUTES = 48 * 60;

const useEarlyBirdsCounter = () => {
  "use no memo";
  const account = useAppSelector(getAccount);
  const isSubscribed = useAppSelector(getIsSubscribed);
  const [count, setCount] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [showCounter, setShowCounter] = useState(false);
  const [isEarlyBirds, setIsEarlyBirds] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownEndDateRef = useRef<Date | null>(null);

  const padNumber = (num: number): string => {
    return num.toString().padStart(2, "0");
  };

  const setIsEarlyBirdsStatus = (createdDate: Date) => {
    const now = new Date().getTime();
    const endDate = new Date(createdDate);
    endDate.setMinutes(
      createdDate.getMinutes() + EARLY_BIRDS_PERIOD_IN_MINUTES,
    );
    setIsEarlyBirds(now < endDate.getTime());
  };

  const startCountdown = () => {
    if (!account || isSubscribed) return;

    const orgCreatedAt = new Date(account.createdAt);
    countdownEndDateRef.current = new Date(account.createdAt);
    countdownEndDateRef.current.setMinutes(
      orgCreatedAt.getMinutes() + EARLY_BIRDS_PERIOD_IN_MINUTES,
    );

    setIsEarlyBirdsStatus(orgCreatedAt);

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start the interval
    intervalRef.current = setInterval(() => {
      const now = new Date().getTime();
      const distance = countdownEndDateRef.current!.getTime() - now;

      if (!showCounter) {
        setShowCounter(true);
      }

      if (distance < 0) {
        setCount({
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
        setIsEarlyBirds(false);
        setIsEarlyBirdsStatus(orgCreatedAt);

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        setCount({
          hours: Math.floor(distance / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        });
      }
    }, 1000);
  };

  useEffect(() => {
    if (!account || isSubscribed) return;

    startCountdown();

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [account, isSubscribed]);

  return {
    count: {
      hours: padNumber(count.hours),
      minutes: padNumber(count.minutes),
      seconds: padNumber(count.seconds),
    },
    showCounter,
    isEarlyBirds: !isSubscribed && isEarlyBirds,
  };
};

export default useEarlyBirdsCounter;
